import { useEffect, useState } from 'react';
import { Result, Spin } from 'antd';
import { Button } from '@bettercomp/ui-components';
import { mapHostedUiError, decodeStatePath, buildDeniedUrl, navigateExternal, type SessionOutcome } from '@bettercomp/ams-spa-auth';
import { useAuthContext } from './AuthContext';
import { RetryableError } from './RetryableError';

export interface CallbackViewProps {
  /** The hosted-UI callback's `code` query parameter, null when absent. */
  code: string | null;
  /** The hosted-UI callback's `state` query parameter; pass '' when absent. */
  state: string;
  /** The hosted-UI callback's `error` query parameter, null when absent. */
  error: string | null;
  /** Replace-style client-side navigation. Must be referentially stable across renders. */
  navigateReplace: (path: string) => void;
}

export function CallbackView({ code, state, error, navigateReplace }: CallbackViewProps) {
  const { session, reportOutcome } = useAuthContext();
  const [result, setResult] = useState<SessionOutcome>();

  useEffect(() => {
    if (!code && !error) {
      console.error('AMS auth callback reached with no code or error parameter, redirecting home');
      navigateReplace('/');
      return;
    }
    if (!session) {
      return;
    }
    if (code) {
      void session.completeLogin({ code, state }).then((outcome) => {
        if (outcome.kind === 'minted') {
          reportOutcome(outcome);
          navigateReplace(decodeStatePath(state));
          return;
        }
        if (outcome.kind === 'terminal') {
          navigateReplace(buildDeniedUrl(outcome));
          return;
        }
        setResult(outcome);
      });
      return;
    }
    if (error) {
      const outcome = mapHostedUiError(error);
      if (outcome.kind === 'terminal') {
        navigateReplace(buildDeniedUrl(outcome));
        return;
      }
      setResult(outcome);
    }
  }, [session, code, state, error, navigateReplace, reportOutcome]);

  const handleSignInAgain = () => {
    void session?.beginLogin(decodeStatePath(state)).then((loginResult) => {
      if (loginResult.kind === 'redirect') {
        navigateExternal(loginResult.url);
        return;
      }
      setResult(loginResult);
    });
  };

  if (!code && !error) {
    return null;
  }

  if (!result) {
    return <Spin data-testid="auth-callback-loading" />;
  }

  if (result.kind === 'sessionExpired') {
    return (
      <Result
        data-testid="auth-session-expired"
        status="info"
        title="Your sign-in link expired"
        subTitle="Please sign in again to continue."
        extra={
          <Button type="primary" onClick={handleSignInAgain}>
            Sign in again
          </Button>
        }
      />
    );
  }

  return <RetryableError onRetry={handleSignInAgain} label="Sign in again" />;
}
